import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import Dialog from '@material-ui/core/Dialog'
import CloseIcon from '@material-ui/icons/Close'
import { useSelector, useDispatch } from 'react-redux'
import moment from 'moment'

import styles from './styles.module.scss'

import { getCompanyAssets, getCompanyEmployeesAll, createMaintenanceRecord } from '../../../api'
import { hideMaintenance } from '../../../store/asset/actions'
import useNotify from '../../../hooks/useNotify'
import { isImage } from '../../../helpers/isImage'

import Input from '../../Core/Input/Input'
import Label from '../../Core/InputLabel'
import InputSelect from '../../Core/InputSelect'
import UploadFiles from '../../UploadFiles'

const getPlanOptions = (asset) => {
  return [...(asset?.maintenance_plan || []), {id: null, name: 'Unplanned repair'}]
}

const createMaintenanceAssetData = (form) => {
  const formData = new FormData()
  formData.append('asset_id', form.asset_id)
  if (form.maintenance_plan_id) {
    formData.append('maintenance_plan_id', form.maintenance_plan_id)
  }
  if (form.employee_id) {
    formData.append('employee_id', form.employee_id)
  }
  formData.append('supplier', form.supplier)
  formData.append('date', form.date)
  if (form.cost) {
    formData.append('cost', form.cost)
  }
  if (form.work_performed) {
    formData.append('work_performed', form.work_performed)
  }
  if (form.invoice_number) {
    formData.append('invoice_number', form.invoice_number)
  }
  if (form.notes) {
    formData.append('notes', form.notes)
  }

  form.documents.forEach((document, index) => {
    formData.append(`documents[${index}][type]`, document.type)
    formData.append(`documents[${index}][name]`, document.name || document.file.name)
    if (document.file) {
      formData.append(`documents[${index}][file]`, document.file)
    }
    if (document.id) {
      formData.append(`documents[${index}][id]`, document.id)
    }
  })
  return formData
}

const MaintenanceRecordForm = forwardRef(({ companyId }, ref) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const notify = useNotify()

  const { show, data } = useSelector(state => state.asset.maintenance)

  const uploadFilesRef = useRef(null)

  const [form, setForm] = useState({
    maintenance_plan_id: null,
    asset_id: null,
    employee_id: null,
    supplier: '',
    date: moment().format('YYYY-MM-DD'),
    documents: []
  })
  const [errors, setErrors] = useState({asset_id: null, supplier: null})
  const [{assets, employees}, setData] = useState({assets: [], employees: []})

  const selectedAsset = assets.find(asset => asset.id === form.asset_id)
  const selectedPlan = selectedAsset?.maintenance_plan?.find(plan => plan.id === form.maintenance_plan_id)

  useEffect(() => {
    if (show) {
      init()

      if (data) {
        setForm(prev => ({
          ...prev,
          ...data,
        }))
      }
    }
  }, [show, data])

  const init = async () => {
    const [assetsRes, employeesRes] = await Promise.all([
      getCompanyAssets(companyId, {with_plan: true}),
      getCompanyEmployeesAll(companyId),
    ])
    if (Array.isArray(assetsRes) && Array.isArray(employeesRes?.users)) {
      setData({assets: assetsRes, employees: employeesRes.users.map(u => ({id: u.id, name: `${u.name} ${u.surname}`}))})
    }
  }

  const handleChangeForm = (e) => {
    const { name, value, type, checked } = e.target
    setForm(prevForm => ({
      ...prevForm,
      [name]: type === 'checkbox' ? checked : value
    }))
    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: null
      }))
    }
  }

  const close = useCallback(() => {
    dispatch(hideMaintenance())
  }, [])

  const validateForm = () => {
    const newErrors = {}
    if (!form.asset_id) {
      newErrors.asset_id = t('Asset is required')
    }
    if (!form.supplier) {
      newErrors.supplier = t('Supplier is required')
    }
    if (selectedPlan) {
      if (selectedPlan.requires_attachments) {
        if (!uploadFilesRef.current.hasFiles()) {
          newErrors.attachments = t('Attachments are required')
        }
        if (!uploadFilesRef.current.hasPhotos()) {
          newErrors.photos = t('Photos are required')
        }
      }
    }
    setErrors(newErrors)
    return Object.values(newErrors).some(error => Boolean(error))
  }

  const handleSubmit = async () => {
    if (validateForm()) {
      return
    }
    const files = uploadFilesRef.current.getFiles()
    const post = {
      asset_id: form.asset_id,
      maintenance_plan_id: form.maintenance_plan_id,
      employee_id: form.employee_id,
      supplier: form.supplier,
      date: form.date,
      cost: form.cost,
      work_performed: form.work_performed,
      invoice_number: form.invoice_number,
      notes: form.notes,
      documents: files.map(file => {
        return {
          file: file,
          name: file.name,
          type: isImage(file.type) ? 'photo' : 'attachment'
        }
      }),
    }
    const formData = createMaintenanceAssetData(post)
    const res = await createMaintenanceRecord(companyId, formData)
    if (res) {
      notify.show(t('Maintenance record created successfully'))
      window.dispatchEvent(
        new CustomEvent('maintenance-record-created')
      )
      close()
    }
  }

  const plansOptions = getPlanOptions(selectedAsset)

  return (
    <Dialog
      onClose={close}
      open={show}
      maxWidth={'md'}
      PaperProps={{ style: { width: '100%' } }}
    >
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            { t('Maintenance Record') }
          </div>
          <div className={styles.closeButton} onClick={close}>
            <CloseIcon />
          </div>
        </div>
        <div className={styles.content}>
          <div className={styles.section}>
            <div className={styles.formRow}>
              <div className={styles.formItem}>
                <Label text={<>{t('Asset')}</>} htmlFor='asset_id' />
                <InputSelect
                  id='asset_id'
                  options={assets}
                  value={form.asset_id}
                  name='asset_id'
                  onChange={handleChangeForm}
                  valueKey='id'
                  labelKey='name'
                  readOnly={data?.asset_id}
                  error={errors.asset_id}
                />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formItem}>
                <Label text={<>{t('Maintenance plan')}</>} htmlFor='trimaintenance_plan_idger' />
                <InputSelect
                  id='maintenance_plan_id'
                  options={plansOptions}
                  value={form.maintenance_plan_id}
                  name='maintenance_plan_id'
                  onChange={handleChangeForm}
                  valueKey='id'
                  labelKey='name'
                />
              </div>
              <div className={styles.formItem}>
                <Label text={<>{t('Performed by')}</>} htmlFor='employee_id' />
                <InputSelect
                  id='employee_id'
                  options={employees}
                  value={form.employee_id}
                  name='employee_id'
                  onChange={handleChangeForm}
                  valueKey='id'
                  labelKey='name'
                />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formItem}>
                <Label text={<>{t('Supplier')}</>} htmlFor='supplier' />
                <Input
                  id='supplier'
                  name='supplier'
                  type='text'
                  placeholder={t('Enter supplier')}
                  value={form.supplier}
                  onChange={handleChangeForm}
                  fullWidth
                  error={errors.supplier}
                />
              </div>
              <div className={styles.formItem}>
                <Label text={<>{t('Date')}</>} htmlFor='date' />
                <Input
                  id='date'
                  name='date'
                  type='date'
                  value={form.date}
                  onChange={handleChangeForm}
                  fullWidth
                />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formItem}>
                <Label text={<>{t('Cost')}</>} htmlFor='cost' />
                <Input
                  id='cost'
                  name='cost'
                  type='number'
                  value={form.cost}
                  onChange={handleChangeForm}
                  fullWidth
                />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formItem}>
                <Label text={<>{t('Work performed')}</>} htmlFor='work_performed' />
                <Input
                  id='work_performed'
                  name='work_performed'
                  type='text'
                  value={form.work_performed}
                  onChange={handleChangeForm}
                  fullWidth
                />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formItem}>
                <Label text={<>{t('Invoice number')}</>} htmlFor='invoice_number' />
                <Input
                  id='invoice_number'
                  name='invoice_number'
                  type='text'
                  value={form.invoice_number}
                  onChange={handleChangeForm}
                  fullWidth
                />
              </div>
            </div>
            <div className={styles.formRow}>
              <div className={styles.formItem}>
                <Label text={<>{t('Notes')}</>} htmlFor='notes' />
                <textarea
                  id='notes'
                  name='notes'
                  placeholder={t('Enter notes')}
                  value={form.notes}
                  onChange={handleChangeForm}
                  rows={4}
                  className={styles.textarea}
                />
              </div>
            </div>
            <UploadFiles ref={uploadFilesRef} />
          </div>
        </div>
        <div className={styles.footer}>
          <button type="button" className={styles.cancelButton} onClick={close}>
            { t('Cancel') }
          </button>
          <button type="button" className={styles.saveButton} onClick={handleSubmit}>
            { t('Save')  }
          </button>
        </div>
      </div>
    </Dialog>
  )
})

export default MaintenanceRecordForm
